// Kiểu mô tả "lá" (leaf) trong cây cấu hình 1 phòng ban — tách riêng khỏi
// department-metadata.ts (nơi khai dữ liệu thật cho 12 ô) để file dữ
// liệu không phình quá 200 dòng, và để RuleTreeEditor/TierListEditor import
// type mà không kéo theo toàn bộ bảng dữ liệu.

export type ScalarLeafKind =
  | "vnd"
  // Ô trả theo việc (CEO chốt 14a 16/09/2026): sổ vẫn ghi ĐỒNG, màn hiện và
  // nhận bằng thạch lương thảo. Chỉ gắn cho ô máy quy đổi (thẻ `pay:"unit"`
  // bên Go) — lương cứng, thưởng, trần thưởng vẫn là "vnd". Gắn nhầm một ô là
  // người sửa gõ 3 tưởng 30.000đ vào ô lương cứng;
  // __tests__/pay-unit-leaves.golden.test.ts khoá danh sách theo máy.
  | "pay-unit"
  | "percent"
  | "count"
  | "boolean"
  | "month"
  // Ba kiểu dưới đây sinh ra cùng sổ bậc tay nghề (quyết định 0104):
  //  - coefficient: hệ số nhân, số thập phân (0,833 · 1 · 1,167). KHÔNG phải
  //    phần trăm — hệ số 1,167 nghĩa là "gấp 1,167 lần", hiện thành 116,7%
  //    thì người đọc tưởng đang cộng thêm 116,7%.
  //  - tier: mã bậc, chọn trong danh sách đóng G1..G5. Gõ tay được thì một ký
  //    tự sai làm máy tính lương nổ giữa tháng.
  //  - date: ngày dạng năm-tháng-ngày. Sai định dạng thì máy đọc thành "rào
  //    còn hiệu lực mãi mãi" — im lặng, và không ai bao giờ rơi bậc.
  | "coefficient"
  | "tier"
  | "date"
  // Hai kiểu của thẻ Phòng Kinh doanh (QĐ 0483): máy đội bán hàng lưu tỷ lệ và
  // hệ số bằng PHẦN VẠN (170 = 1,7%; 12.000 = ×1,2), không phải phân số như các
  // thẻ khác. Ô hiện phần trăm / hệ số y như "percent" / "coefficient" và quy
  // về phần vạn khi lưu (bp-units.ts) — gắn nhầm "percent" là lệch một vạn lần.
  | "bp-percent"
  | "bp-coefficient";

/**
 * PHẦN LƯƠNG — một khoản trong bảng lương của MỘT người, xếp đúng thứ tự máy
 * cộng tiền (11/09/2026).
 *
 * Trước đó màn xếp theo cách khác: ô số gom theo LOẠI HỢP ĐỒNG (Fulltime ·
 * Parttime · CTV), rồi mọi bảng đổ xuống cuối — nên "Phân loại đơn" (thứ
 * quyết định đơn ăn ô giá nào) và "Phát sinh" nằm SAU cả mốc thưởng và tăng
 * ca, tức sau những thứ phụ thuộc vào chúng. Đọc từ trên xuống không ra được
 * cách tính lương một người.
 *
 * Thứ tự trong danh sách này LÀ thứ tự hiện trên màn.
 */
export const PAY_SECTIONS = [
  "base",
  "perWork",
  "surcharge",
  "commission",
  "bonus",
  "overtime",
  // Định mức tháng (29/09/2026) — KHÔNG phải một khoản tiền: máy chỉ đóng dấu
  // xếp hạng. Đứng sau mọi phần lương để đọc xuôi không tưởng nó cộng vào tổng.
  "quota",
  "other",
] as const;

export type PaySection = (typeof PAY_SECTIONS)[number];

export const PAY_SECTION_LABEL: Record<PaySection, string> = {
  base: "Lương cứng",
  perWork: "Tiền theo việc",
  surcharge: "Phát sinh",
  commission: "Hoa hồng",
  bonus: "Thưởng",
  overtime: "Tăng ca",
  quota: "Định mức tháng (xếp hạng)",
  other: "Khác",
};

export interface ScalarLeafMeta {
  /** Đường dẫn chấm bên trong config của phòng (dùng cho path-utils.ts). */
  path: string;
  label: string;
  kind: ScalarLeafKind;
  /** Phần lương chứa ô này. Vắng = "other", rơi xuống cuối màn. */
  section?: PaySection;
  /**
   * Chú thích ngắn hiện dưới MỘT ô — dành cho ô có luật RIÊNG của nó.
   *
   * Luật chung cho cả một nhóm ô thì khai ở chú thích KHỐI
   * (`PAY_SECTION_HINTS` trong department-metadata.ts), đừng lặp xuống từng
   * lá: chín ô cùng một câu là chín lần đọc lại một dòng, và người sửa thôi
   * đọc từ ô thứ hai.
   */
  hint?: string;
}

/**
 * 7 dạng mảng/map tồn tại trong Rules — mỗi dạng có ngữ nghĩa sắp xếp/tính
 * tiền riêng, KHÔNG được dùng chung 1 UI logic (xem TierListEditor.tsx):
 *  - milestone-count / milestone-money: TĂNG dần theo threshold, chỉ mốc CAO
 *    NHẤT đã đạt được trả (không cộng dồn).
 *  - leader-tier: bậc thang hoa hồng luỹ tiến — phần doanh thu TRÊN mỗi mốc
 *    ăn rate của mốc đó (không phải toàn bộ).
 *  - upsell-tier: sắp GIẢM dần theo threshold, quét từ đầu, khớp đầu tiên ăn
 *    trên TOÀN BỘ doanh thu (không phải phần vượt) — UI phải tự sắp giảm
 *    dần sau mỗi lần thêm/sửa/xoá dòng vì đây là thứ tự nghiệp vụ pickUpsellTier
 *    (Go) cần, không phải thứ tự người dùng gõ vào (xem TierListEditor.tsx).
 *  - streak-tier: TĂNG dần theo days, CỘNG DỒN (khác milestone).
 *  - string-list: danh sách tên phòng ban thuần (Depts).
 *  - vnd-map: Record<tên phòng, số tiền> (Champion.MinOutput).
 *
 * 🔴 BỐN dạng dưới đây đều là "mảng chuỗi" khi nhìn vào JSON, nhưng KHÔNG được gộp
 * làm một kiểu. Mảng chuỗi không tự nói nó chứa mã gì, nên gộp lại là bày sai
 * danh sách lựa chọn cho người bấm — và hậu quả IM LẶNG:
 *  - string-list  → mã phòng ban của tính năng THI ĐUA (6 phòng biết xếp hạng).
 *  - craft-dept-list → mã phòng ban mà cơ chế BẬC TAY NGHỀ chạm tới. Chỉ ba:
 *    photo_idphoto · photo_profile · makeup (xem craftWorkerOf trong Go). Bày
 *    nhầm danh sách thi đua thì vừa thiếu photo_profile — đúng nhóm cơ chế này
 *    sinh ra để trả — vừa thừa retouch/cskh/reception/sale, tick vào không báo
 *    lỗi gì mà cũng chẳng chạm ai.
 *  - craft-block-list → mã KHỐI NGHỀ (photo · makeup). Máy chủ chặn mã lạ khi
 *    lưu, nên bày sai ở đây chỉ tổ cho người bấm ăn lỗi 400 không hiểu vì sao.
 *  - branch-list → mã CƠ SỞ, lấy từ danh mục chi nhánh thật. Trục này KHÔNG
 *    được kiểm khi lưu: ghi nhầm một mã phòng ban vào đây thì lưu êm, công tắc
 *    không chạm ai, và cả tháng sau mới có người thắc mắc.
 */
export type TierKind =
  | "milestone-count"
  | "milestone-money"
  | "leader-tier"
  | "upsell-tier"
  | "streak-tier"
  | "string-list"
  | "craft-dept-list"
  | "craft-block-list"
  | "branch-list"
  | "vnd-map"
  // Bốn dạng thêm 10/09/2026 cho các luật vừa rời khỏi mã nguồn:
  //  - headcount-band: thang "bao nhiêu người thì mấy công" — nấc phải nối
  //    nhau, nấc cuối trần 0; máy chủ chặn hở/chồng khi lưu.
  //  - concept-band: thang tỷ lệ concept 2 theo tổng set phát sinh trong tháng.
  //  - product-list: danh sách MÃ GÓI, chọn từ danh mục sản phẩm thật (không
  //    gõ tay — gõ sai một ký tự là gói rơi khỏi luật, im lặng).
  //  - surcharge-code-list: danh sách MÃ MỤC PHÁT SINH, chọn từ bảng giá phát
  //    sinh thật; cùng lý do.
  //  - text-list: chuỗi tự do (tiền tố mã gói, chữ dò trên nhãn) — mỗi dòng
  //    một mục, máy chủ chuẩn hoá về chữ thường với nhãn.
  //  - set-catalog: bản đồ mã gói → loại buổi + cờ ngoại cảnh, bộ soạn riêng.
  | "headcount-band"
  | "concept-band"
  // Hai kind của BẢNG LUẬT PHÁT SINH (QĐ 0115) — mỗi thẻ vai một bảng, mỗi
  // dòng một phát sinh. Tách hai kind vì phạm vi khác nhau: thẻ ảnh thẻ chỉ có
  // đơn ảnh thẻ, thẻ profile có cả đơn profile lẫn buổi chụp Chụp Ảnh. Gộp
  // một kind là bày cho kế toán thêm rule vào phạm vi mà máy chủ sẽ từ chối.
  | "surcharge-rules-at"
  | "surcharge-rules-profile"
  // Chỉ nhóm "Buổi chụp Chụp Ảnh" của cùng bảng — thẻ gom số CA hiện lại phần
  // bảng Phát sinh của thẻ Profile mà buổi Chụp Ảnh đọc (28/09/2026).
  | "surcharge-rules-session"
  | "product-list"
  // Bộ PHÂN LOẠI ĐƠN ảnh thẻ (QĐ 0116) — ba lớp cộng công tắc "xếp theo" gộp
  // một lá, vì công tắc dùng chung: tách ba lá là bày cho kế toán đặt mỗi lớp
  // một kiểu, mà cả 11 gói ảnh thẻ cùng một nhóm nên làm thế là sai âm thầm.
  | "product-class"
  | "surcharge-code-list"
  | "text-list"
  // Bảng "Mức trả theo gói": mỗi dòng {product, units, rate}. Từ quyết định 0174
  // dòng khai bằng CÔNG (units); dòng sổ cũ còn khai tiền thì rate là đồng, màn
  // hiện thạch lương thảo. Dòng bằng 0 KHÁC gói vắng.
  | "product-rates"
  | "set-catalog"
  // Nấc thưởng tiểu đội trưởng (QĐ 0483): mỗi dòng {uptoVnd, rateBp} — mốc TRÊN
  // tăng dần, nấc cuối mốc 0 = không giới hạn; tỷ lệ bằng phần vạn.
  | "squad-lead-tier";

export interface ArrayLeafMeta {
  path: string;
  label: string;
  kind: TierKind;
  /** Phần lương chứa bảng này. Vắng = "other", rơi xuống cuối màn. */
  section?: PaySection;
  /** Chú thích ngắn cho các bộ soạn danh sách mã/chữ — máy làm gì với danh sách này. */
  hint?: string;
}

export interface DepartmentMeta {
  scalars: ScalarLeafMeta[];
  arrays: ArrayLeafMeta[];
}
